import { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const SCREENSHOTS = [
  { src: '/images/ios/screenshot-1.png', captionKey: 'screenshots.home1' },
  { src: '/images/ios/screenshot-2.png', captionKey: 'screenshots.home2' },
  { src: '/images/ios/screenshot-3.png', captionKey: 'screenshots.message' },
  { src: '/images/ios/screenshot-4.png', captionKey: 'screenshots.translate' },
  { src: '/images/ios/screenshot-5.png', captionKey: 'screenshots.media' },
];

const FEATURES = [
  { icon: 'bi-chat-text', key: 'features.list.0' },
  { icon: 'bi-bell',      key: 'features.list.1' },
  { icon: 'bi-download',  key: 'features.list.2' },
  { icon: 'bi-translate', key: 'features.list.3' },
];

const ADMIN_EMAIL = 'admin@nogi46.me';

const IOSAppPage = () => {
  const { t } = useTranslation('iosApp');
  const [lightbox, setLightbox] = useState(null);

  document.title = 'SakaMessage iOS';

  return (
    <>
      <style>{`
        .ios-screenshot-scroll::-webkit-scrollbar { display: none; }
        .ios-screenshot-item {
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        @media (hover: hover) {
          .ios-screenshot-item:hover {
            transform: scale(1.06);
            box-shadow: 0 12px 32px rgba(0,0,0,0.22) !important;
          }
        }
      `}</style>

      {/* Lightbox overlay */}
      {lightbox !== null && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <img
            src={SCREENSHOTS[lightbox].src}
            alt=""
            style={{
              maxHeight: '88vh',
              maxWidth: '100%',
              objectFit: 'contain',
              borderRadius: '16px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed',
              top: '20px',
              right: '24px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              color: '#fff',
              fontSize: '1.2rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      <div className="w-100 d-flex justify-content-center">
        <div className="py-5 px-3 w-100" style={{ maxWidth: '760px' }}>

          {/* 標題區 */}
          <div className="text-center mb-5">
            <i className="bi bi-apple" style={{ fontSize: '3rem', color: '#1c1c1e' }}></i>
            <div className="mt-3 mb-2">
              <Badge bg="success" style={{ fontSize: '0.75rem' }}>
                {t('badge')}
              </Badge>
            </div>
            <h1 className="fw-bold mb-2" style={{ fontSize: '2rem' }}>{t('title')}</h1>
            <p className="text-muted fs-5 mb-3">{t('subtitle')}</p>
            <p className="text-muted" style={{ maxWidth: '520px', margin: '0 auto' }}>
              {t('description')}
            </p>
          </div>

          {/* 定價 + 按鈕 */}
          <div className="text-center mb-5">
            <div
              className="d-inline-block px-4 py-3 mb-4"
              style={{ background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}
            >
              <i className="bi bi-tag me-2 text-primary"></i>
              <span className="fw-semibold">{t('pricing')}</span>
            </div>
            <div>
              <Button
                variant="dark"
                size="lg"
                href={`mailto:${ADMIN_EMAIL}`}
                style={{ borderRadius: '12px', padding: '12px 32px' }}
              >
                <i className="bi bi-apple me-2"></i>
                {t('contactAdmin')}
              </Button>
            </div>
          </div>

          {/* 主要功能 */}
          <div className="mb-5">
            <h5 className="fw-semibold mb-3 text-center">{t('features.title')}</h5>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              {FEATURES.map((f) => (
                <div
                  key={f.key}
                  className="text-center py-3 px-3"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid #e9ecef',
                    minWidth: '120px',
                    flex: '1 1 120px',
                    maxWidth: '180px',
                  }}
                >
                  <i className={`bi ${f.icon} mb-2`} style={{ fontSize: '1.5rem', color: '#0d6efd', display: 'block' }}></i>
                  <small className="text-muted">{t(f.key)}</small>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* 截圖區 — 全寬，不受 maxWidth 限制，hover 放大才不會被裁 */}
      <div className="mb-5 px-4">
        <h5 className="fw-semibold mb-4 text-center">{t('screenshots')}</h5>
        <div
          className="ios-screenshot-scroll"
          style={{ overflowX: 'auto', msOverflowStyle: 'none' }}
        >
          <div
            style={{
              display: 'flex',
              gap: '16px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              paddingTop: '20px',
              paddingBottom: '20px',
              paddingLeft: '8px',
              paddingRight: '8px',
              justifyContent: 'center',
            }}
          >
            {SCREENSHOTS.map((shot, idx) => (
              <div
                key={shot.captionKey}
                className="ios-screenshot-item"
                onClick={() => setLightbox(idx)}
                style={{
                  flex: '0 0 clamp(140px, 18vw, 210px)',
                  scrollSnapAlign: 'start',
                  aspectRatio: '9 / 19.5',
                }}
              >
                <img
                  src={shot.src}
                  alt={shot.captionKey}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: '20px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        </div>
        <p className="text-muted text-center mt-1" style={{ fontSize: '0.78rem' }}>
          <i className="bi bi-arrows-fullscreen me-1"></i>
          {t('screenshotHint')}
        </p>
      </div>

      {/* 取得方式說明 */}
      <div className="w-100 d-flex justify-content-center pb-5 px-3">
        <div
          className="text-center py-4 px-4 w-100"
          style={{ maxWidth: '760px', background: '#f8f9fa', borderRadius: '16px', border: '1px solid #e9ecef' }}
        >
          <p className="fw-semibold mb-1">{t('contact.label')}</p>
          <p className="text-muted small mb-3">{t('contact.description')}</p>
          <Button variant="outline-dark" href={`mailto:${ADMIN_EMAIL}`} size="sm" style={{ borderRadius: '8px' }}>
            <i className="bi bi-envelope me-2"></i>
            {ADMIN_EMAIL}
          </Button>
        </div>
      </div>
    </>
  );
};

export default IOSAppPage;
